import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { progressReviewApi } from '../api/progress-reviews';
import { apprenticeApi } from '../api/apprentices';
import { ProgressReview } from '../types/progress-review.types';
import { Apprentice } from '../types';

const KSB_RATINGS: any = {
  'not-started': { label: 'Not Started', color: 'bg-gray-100 text-gray-800' },
  'developing': { label: 'Developing', color: 'bg-yellow-100 text-yellow-800' },
  'competent': { label: 'Competent', color: 'bg-blue-100 text-blue-800' },
  'proficient': { label: 'Proficient', color: 'bg-green-100 text-green-800' },
};

export const ProgressReviewDetail: React.FC = () => {
  const { user, userRole } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [review, setReview] = useState<ProgressReview | null>(null);
  const [apprentice, setApprentice] = useState<Apprentice | null>(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [isMyReview, setIsMyReview] = useState(false);

  useEffect(() => {
    const loadReview = async () => {
      if (!user?.tenant_id || !id) return;

      try {
        setLoading(true);
        const reviewRes = await progressReviewApi.getOne(user.tenant_id, id);
        setReview(reviewRes.data);

        const apprenticeRes = await apprenticeApi.getOne(user.tenant_id, reviewRes.data.apprentice_id);
        setApprentice(apprenticeRes.data);

        // Check if this is the learner's own review
        if (userRole === 'learner') {
          const isOwn = apprenticeRes.data.canvas_user_id === user.user_id || 
                        apprenticeRes.data.email === user.email;
          setIsMyReview(isOwn);
        }
      } catch (err) {
        console.error('Failed to load review:', err);
      } finally {
        setLoading(false);
      }
    };

    loadReview();
  }, [user?.tenant_id, user?.user_id, user?.email, userRole, id]);

  const handleSign = async (signatureType: 'employer' | 'apprentice') => {
    if (!user?.tenant_id || !id) return;

    try {
      setSigning(true);
      const response = await progressReviewApi.sign(user.tenant_id, id, signatureType);
      setReview(response.data);
    } catch (err) {
      console.error('Failed to sign review:', err);
      alert('Failed to sign review');
    } finally {
      setSigning(false);
    }
  };

  const handleDelete = async () => {
    if (!user?.tenant_id || !id) return;
    if (!window.confirm('Are you sure you want to delete this progress review?')) return;

    try {
      await progressReviewApi.delete(user.tenant_id, id);
      navigate('/progress-reviews');
    } catch (err) {
      console.error('Failed to delete review:', err);
      alert('Failed to delete review');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const getReviewTypeColor = (type: string) => {
    const colors: any = {
      'Initial': 'bg-blue-100 text-blue-800',
      'Mid-point': 'bg-yellow-100 text-yellow-800',
      'Quarterly': 'bg-purple-100 text-purple-800',
      'Final': 'bg-green-100 text-green-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading review...</div>
      </div>
    );
  }

  if (!review || !apprentice) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-800">Progress review not found</p>
        </div>
      </div>
    );
  }

  // Learners can only sign their own reviews as apprentice
  const canSignAsApprentice = userRole === 'learner' && isMyReview;
  // Instructors and admins can sign as employer
  const canSignAsEmployer = userRole === 'instructor' || userRole === 'administrator';
  const canDelete = userRole === 'instructor' || userRole === 'administrator';

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <Link to="/progress-reviews" className="text-blue-600 hover:text-blue-700 text-sm mb-2 inline-block">
            ← Back to Reviews
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Progress Review</h1>
        </div>
        <div className="flex gap-3">
          <a
            href={`/api/progress-reviews/tenant/${user?.tenant_id}/${id}/pdf`}
            download
            className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition"
          >
            Download PDF
          </a>
          {canDelete && (
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition"
            >
              Delete Review
            </button>
          )}
        </div>
      </div>

      {/* Apprentice needs to sign notice */}
      {canSignAsApprentice && !review.apprentice_signature && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-blue-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-blue-800 mb-1">Action Required: Sign Your Progress Review</h3>
              <p className="text-sm text-blue-700">
                Please review the progress summary and targets below, then sign to confirm you have read and understood the review.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Apprentice Info */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            {userRole !== 'learner' && (
              <>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {apprentice.first_name} {apprentice.last_name}
                </h2>
                <p className="text-sm text-gray-600">{apprentice.email}</p>
              </>
            )}
            {userRole === 'learner' && (
              <h2 className="text-xl font-semibold text-gray-900">Your Progress Review</h2>
            )}
          </div>
          <span className={`px-3 py-1 text-sm font-semibold rounded-full ${getReviewTypeColor(review.review_type)}`}>
            {review.review_type} Review
          </span>
        </div>
        <div className="text-sm text-gray-600">
          Review Date: <span className="font-medium text-gray-900">{formatDate(review.review_date)}</span>
        </div>
      </div>

      {/* Progress Summary */}
      {review.progress_summary && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Progress Summary</h3>
          <p className="text-gray-700 whitespace-pre-wrap">{review.progress_summary}</p>
        </div>
      )}

      {/* Targets Set */}
      {review.targets_set && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Targets for Next Period</h3>
          <p className="text-gray-700 whitespace-pre-wrap">{review.targets_set}</p>
        </div>
      )}

      {/* KSB Progress */}
      {review.ksb_progress && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">KSB Progress Ratings</h3>
          
          {review.ksb_progress.skills && Object.keys(review.ksb_progress.skills).length > 0 && (
            <div className="mb-6">
              <h4 className="font-medium text-gray-900 mb-3">Skills</h4>
              <div className="space-y-2">
                {Object.entries(review.ksb_progress.skills).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <span className="font-medium text-gray-900">{key}</span>
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${KSB_RATINGS[value as string]?.color || 'bg-gray-100 text-gray-800'}`}>
                      {KSB_RATINGS[value as string]?.label || value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {review.ksb_progress.knowledge && Object.keys(review.ksb_progress.knowledge).length > 0 && (
            <div className="mb-6">
              <h4 className="font-medium text-gray-900 mb-3">Knowledge</h4>
              <div className="space-y-2">
                {Object.entries(review.ksb_progress.knowledge).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <span className="font-medium text-gray-900">{key}</span>
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${KSB_RATINGS[value as string]?.color || 'bg-gray-100 text-gray-800'}`}>
                      {KSB_RATINGS[value as string]?.label || value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {review.ksb_progress.behaviours && Object.keys(review.ksb_progress.behaviours).length > 0 && (
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Behaviours</h4>
              <div className="space-y-2">
                {Object.entries(review.ksb_progress.behaviours).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <span className="font-medium text-gray-900">{key}</span>
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${KSB_RATINGS[value as string]?.color || 'bg-gray-100 text-gray-800'}`}>
                      {KSB_RATINGS[value as string]?.label || value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Signatures */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Signatures</h3>
        
        <div className="space-y-4">
          {/* Employer Signature */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${review.employer_signature ? 'bg-green-100' : 'bg-orange-100'}`}>
                {review.employer_signature ? (
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>
              <div>
                <div className="font-medium text-gray-900">Employer/Assessor Signature</div>
                <div className="text-sm text-gray-600">
                  {review.employer_signature ? 'Signed' : 'Pending signature'}
                </div>
              </div>
            </div>
            {!review.employer_signature && canSignAsEmployer && (
              <button
                onClick={() => handleSign('employer')}
                disabled={signing}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:bg-gray-400"
              >
                {signing ? 'Signing...' : 'Sign as Employer'}
              </button>
            )}
          </div>

          {/* Apprentice Signature */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${review.apprentice_signature ? 'bg-green-100' : 'bg-orange-100'}`}>
                {review.apprentice_signature ? (
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>
              <div>
                <div className="font-medium text-gray-900">Apprentice Signature</div>
                <div className="text-sm text-gray-600">
                  {review.apprentice_signature ? 'Signed' : 'Pending signature'}
                </div>
              </div>
            </div>
            {!review.apprentice_signature && canSignAsApprentice && (
              <button
                onClick={() => handleSign('apprentice')}
                disabled={signing}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:bg-gray-400"
              >
                {signing ? 'Signing...' : 'Sign Review'}
              </button>
            )}
            {!review.apprentice_signature && !canSignAsApprentice && userRole !== 'learner' && (
              <button
                onClick={() => handleSign('apprentice')}
                disabled={signing}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:bg-gray-400"
              >
                {signing ? 'Signing...' : 'Sign as Apprentice'}
              </button>
            )}
          </div>
        </div>

        {review.employer_signature && review.apprentice_signature && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm text-green-800 font-medium">
              ✓ This review has been signed by both parties
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
